import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getErrorString } from 'src/utils'
import {
  changeLogLevel,
  decrypt,
  deleteAWSSubscriber,
  deleteFullOrganization,
  deleteOrgRegion,
  editAdminOrganization,
  encrypt,
  getAdminOrganization,
  getAdminOrganizations,
  getLogLevel,
  getNotMappedRegions,
  getNotMappedSubscribers,
  getOrganizationsLastUsed,
  getServerVersion,
} from './api'
import { toast } from 'sonner'
import { useToast } from 'src/ui/use-toast'

// prettier-ignore
export const keys = {
  all: ['ORG_ADMIN_KEYS'] as const,
  getLogLevel: () => [...keys.all, 'GET_LOG_LEVEL'] as const,
  getAdminOrganizations: () => [...keys.all, 'GET_ADMIN_ORGANIZATIONS'] as const,
  getOrg: (orgId: string) => [...keys.all, 'GET_ADMIN_ORG', orgId] as const,
  getServerVersion: () => [...keys.all, 'GET_ADMIN_SERVER_VERSION'] as const,
}

export function useGetLogLevel() {
  return useQuery(keys.getLogLevel(), getLogLevel, {
    onError: (error) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useChangeLogLevel() {
  return useMutation(changeLogLevel, {
    onError: (error) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useGetAdminOrganizations(
  params: Parameters<typeof getAdminOrganizations>[0],
) {
  return useQuery(
    keys.getAdminOrganizations(),
    () => getAdminOrganizations(params),
    {
      onError: (error) => {
        toast.error(getErrorString(error))
      },
    },
  )
}

export function useGetAdminOrganization(
  params: Parameters<typeof getAdminOrganization>[0],
) {
  return useQuery(
    keys.getOrg(params.organization),
    () => getAdminOrganization(params),
    {
      // staleTime: Infinity,
      onError: (error) => {
        toast.error(getErrorString(error))
      },
    },
  )
}

export function useEditAdminOrganization() {
  const queryClient = useQueryClient()
  return useMutation(editAdminOrganization, {
    onSuccess: async (data, params, context) => {
      // TODO If we don't use setTimeout we'd get old data after editing because reloadoneorganization isn't async
      // setTimeout is bad solution but we need it for now
      setTimeout(
        () => queryClient.invalidateQueries(keys.getOrg(params.organization)),
        1000,
      )
    },
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useEncrypt() {
  return useMutation(['encrypt'], encrypt, {
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useDecrypt() {
  return useMutation(decrypt, {
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useServerVersion() {
  return useQuery(keys.getServerVersion(), () => getServerVersion(), {
    onError: (error) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useNotMappedRegions(
  params: Parameters<typeof getNotMappedRegions>[0],
) {
  return useQuery(
    ['admin-overview-regions'],
    async () => {
      const notMappedRegions = await getNotMappedRegions({
        regionstoscan: params.regionstoscan,
        username: params.username,
      })

      const data = notMappedRegions.missingregions.filter(
        (obj, index) =>
          index ===
          notMappedRegions.missingregions.findIndex(
            (o) => obj.organization === o.organization,
          ),
      )

      return data
    },
    {
      staleTime: 2 * 60 * 1000,
      onError: (error) => {
        toast.error(getErrorString(error))
      },
    },
  )
}

export function useOrganizationsNotMappedSubscribers(
  params: Parameters<typeof getNotMappedSubscribers>[0],
) {
  return useQuery(
    ['admin-overview-subscribers'],
    async () => {
      const notMappedSubscribers = await getNotMappedSubscribers({
        regionstoscan: params.regionstoscan,
        username: params.username,
      })

      const data = notMappedSubscribers.allmissingorganizations.filter(
        (obj, index) =>
          index ===
          notMappedSubscribers.allmissingorganizations.findIndex(
            (o) => obj.customerIdentifier === o.customerIdentifier,
          ),
      )

      return data
    },
    {
      staleTime: 2 * 60 * 1000,
      onError: (error) => {
        toast.error(getErrorString(error))
      },
    },
  )
}

export function useOrganizationsLastUsed(
  params: Parameters<typeof getOrganizationsLastUsed>[0],
) {
  return useQuery(
    ['admin-overview-lastused'],
    async () => {
      const lastUsedOrgs = await getOrganizationsLastUsed({
        regionstoscan: params.regionstoscan,
        username: params.username,
      })

      // Filtering out duplicates just in case
      const data = lastUsedOrgs.organizationslastused.filter(
        (obj, index) =>
          index ===
          lastUsedOrgs.organizationslastused.findIndex(
            (o) => obj.organization === o.organization,
          ),
      )

      return data
    },
    {
      staleTime: 2 * 60 * 1000,
      onError: (error) => {
        toast.error(getErrorString(error))
      },
    },
  )
}

export function useDeleteFullOrganization() {
  const queryClient = useQueryClient()
  const fullToast = useToast().toast
  return useMutation(deleteFullOrganization, {
    onError: (error) => {
      toast.error(getErrorString(error))
    },
    onSuccess: (data) => {
      toast.success('Organization deleted succesfully.')

      fullToast({
        title: 'Steps',
        description: (
          <div className="flex flex-col break-all">
            {data.progress_stack.map((step) => (
              <p className={!step.startsWith('STEP') ? 'mb-4' : ''}>{step}</p>
            ))}
            ,
          </div>
        ),
      })

      /*
      [
        "STEP 1 remove cognito user yannickccptest .",
        "fetching cognito user yannickccptest .",
        "cognito user found [{\"Name\":\"custom:region\",\"Value\":\"eu-central-1\"},{\"Name\":\"custom:organization\",\"Value\":\"yannickccptest\"},{\"Name\":\"sub\",\"Value\":\"875a1bb9-5215-47d2-b90a-ba6a971150a2\"},{\"Name\":\"custom:phone\",\"Value\":\"+32476077374\"},{\"Name\":\"email_verified\",\"Value\":\"True\"},{\"Name\":\"name\",\"Value\":\"Yannick Grenier\"},{\"Name\":\"given_name\",\"Value\":\"Yannick\"},{\"Name\":\"family_name\",\"Value\":\"Grenier\"},{\"Name\":\"email\",\"Value\":\"yannick.grenier@idealsystems.be\"}] .",
        "delete cognito user .",
        "STEP 2 remove region in mc_org_region yannickccptest .",
        "delete entry in MC_ORG_REGION yannickccptest .",
        "STEP 3 remove AWSMarketPlaceSubscriber yannickccptest .",
        "delete entry in AWSSubsctiber table yannickccptest .",
        "STEP 4 remove organization yannickccptest .",
        "delete entry in MC_ORGANIZATION region eu-central-1 yannickccptest .",
        "STEP 5 delete org from organizations memory yannickccptest .",
        "deleteoneorganizationprovisioning done for servers https://dev.mobileconnectcloud.com"
    ]
      */

      queryClient.invalidateQueries(['admin-overview-lastused'])
      queryClient.invalidateQueries(['admin-overview-regions'])
      queryClient.invalidateQueries(['admin-overview-subscribers'])
    },
  })
}

export function useDeleteOrgRegion() {
  const queryClient = useQueryClient()
  return useMutation(deleteOrgRegion, {
    onError: (error) => {
      toast.error(getErrorString(error))
    },
    onSuccess: () => {
      toast.success('Org region succesfully deleted.')
      queryClient.invalidateQueries(['admin-overview-regions'])
    },
  })
}

export function useDeleteAWSSubscriber() {
  const queryClient = useQueryClient()
  return useMutation(deleteAWSSubscriber, {
    onError: (error) => {
      toast.error(getErrorString(error))
    },
    onSuccess: () => {
      toast.success('AWS Subscriber succesfully deleted.')
      queryClient.invalidateQueries(['admin-overview-subscribers'])
    },
  })
}
